import prismaticSDK from '@prismatic-io/embedded';
import { AuthClient } from '@appcues/auth';
import { IntegrationId } from 'studio5/pages/integrations/metadata';
import {
  HUBSPOT_ID,
  HUBSPOT_V2_ID,
  MARKETO_ID,
  SALESFORCE_ID,
  SALESFORCE_V2_ID,
  SLACK_ID,
  ZENDESK_SUPPORT_ID,
} from 'studio5/pages/integrations/metadata/integrations-third-party';
import createCustomerApiClient from './api/customer-api';

export const INTEGRATION_ID_TO_PRISMATIC_NAME: Partial<
  Record<IntegrationId, string>
> = {
  [HUBSPOT_ID]: 'HubSpot Integration',
  [HUBSPOT_V2_ID]: 'HubSpot Integration (Code Native)',
  [SALESFORCE_ID]: 'Salesforce Integration',
  [SALESFORCE_V2_ID]: 'Salesforce Integration (Code Native)',
  [SLACK_ID]: 'Slack Integration',
  [MARKETO_ID]: 'Marketo Integration',
  [ZENDESK_SUPPORT_ID]: 'Zendesk Support Integration',
};

export type PrismaticClient = {
  configureInstance: (integrationName: string) => Promise<void>;
  getAccountIntegrations: () => Promise<PrismaticIntegrationNode[]>;
};

export type PrismaticIntegrationNode = {
  enabled: boolean;
  integration: {
    name: string;
  };
};

const getAccountIntegrationsQuery = `
  query getAccountIntegrations($enabled: Boolean!, $accountId: String!) {
    instances(enabled: $enabled, customer_ExternalId: $accountId) {
      nodes {
        enabled
        integration {
          name
        }
      }
    }
  }
`;

export function createPrismaticClient(
  auth: AuthClient,
  prismaticUrl = import.meta.env.PRISMATIC_URL
): PrismaticClient {
  const customerApiClient = createCustomerApiClient(auth);

  prismaticSDK.init({ prismaticUrl });

  const handlePrismaticAuth = async () => {
    const token = await customerApiClient.getPrismaticAuthToken();
    await prismaticSDK.authenticate({ token });
  };

  return {
    async configureInstance(integrationName) {
      await handlePrismaticAuth();

      prismaticSDK.configureInstance({
        integrationName,
        skipRedirectOnRemove: true,
        usePopover: true,
        theme: 'LIGHT',
        screenConfiguration: {
          marketplace: { configuration: 'disallow-details' },
          instance: {
            hideBackToMarketplace: true,
            hideTabs: ['Test', 'Executions', 'Monitors', 'Logs'],
          },
          configurationWizard: {
            hideSidebar: true,
            isInModal: true,
            triggerDetailsConfiguration: 'hidden',
          },
        },
      });
    },
    async getAccountIntegrations() {
      await handlePrismaticAuth();

      const accountId = await auth.getAccountId();

      const { data } = await prismaticSDK.graphqlRequest({
        query: getAccountIntegrationsQuery,
        variables: { accountId, enabled: true },
      });

      return data?.instances?.nodes || [];
    },
  };
}
